"use client";

import { useTranslations } from "next-intl";
import Link from "next/link";
import {
  FaCalculator,
  FaChartBar,
  FaDatabase,
  FaSearch,
  FaBook,
} from "react-icons/fa";
import packageJson from "@/package.json";
import Header from "@/components/header";
import Footer from "@/components/footer";

export default function DocsPage() {
  const t = useTranslations("docs");
  const headerT = useTranslations("header");

  const features = [
    {
      icon: <FaSearch className="h-6 w-6" />,
      title: t("navigation.authorSearch.title"),
      description: t("navigation.authorSearch.description"),
      href: "/docs/author-search",
    },
    {
      icon: <FaCalculator className="h-6 w-6" />,
      title: t("navigation.metrics.title"),
      description: t("navigation.metrics.description"),
      href: "/docs/metrics",
    },
    {
      icon: <FaChartBar className="h-6 w-6" />,
      title: t("navigation.analysis.title"),
      description: t("navigation.analysis.description"),
      href: "/docs/analysis",
    },
    {
      icon: <FaDatabase className="h-6 w-6" />,
      title: t("navigation.export.title"),
      description: t("navigation.export.description"),
      href: "/docs/export",
    },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 smooth-scroll prose prose-slate max-w-none prose-sm">
      <Header name={t("main.title")} desc={headerT("description")} />

      {/* Hero Section */}
      <section className="relative bg-linear-to-br from-gray-900 to-black text-white not-prose">
        <div className="container mx-auto max-w-7xl px-4 py-12 sm:py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-4">
                {t("main.title")}{" "}
                <span className="text-bme-main">v{packageJson.version}</span>
              </h1>
              <p className="text-white/80 text-base sm:text-lg">
                {t("main.subtitle")}
              </p>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-6">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-4">
                  <div className="text-2xl sm:text-3xl font-bold text-bme-main">4</div>
                  <div className="text-xs text-white/70 mt-1">
                    Documentation Sections
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-2xl sm:text-3xl font-bold text-bme-main">100%</div>
                  <div className="text-xs text-white/70 mt-1">Coverage</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-linear-to-r from-transparent via-bme-main to-transparent opacity-80" />
      </section>

      {/* Features Grid */}
      <section className="container mx-auto max-w-7xl px-4 py-10 sm:py-12 not-prose">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
          {features.map((feature, index) => (
            <Link
              key={index}
              href={feature.href}
              className="group block w-full rounded-xl border border-gray-200 bg-white p-6 hover:border-bme-main hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-linear-to-br from-bme-main/10 to-bme-main/5 p-3 text-bme-main group-hover:from-bme-main group-hover:to-bme-main group-hover:text-white transition-all duration-300 shrink-0">
                  {feature.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
